import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, limit, onSnapshot, updateDoc, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { getMessages, sendMessage, markConversationAsRead } from '../services/chatService';
import { getCurrentUser } from '../services/authService';
import { useWindowSize, BP } from '../hooks/useWindowSize';
import { isTranslationPending } from '../utils/chatTranslationDisplay';
import { resolveMarkAsReadRefire } from '../utils/chatMarkAsReadRefire';
import { filterBlockedMessages } from '../utils/chatMessageVisibility';

const REPORT_REASONS = [
  { value: 'harassment', label: 'Harassment' },
  { value: 'spam', label: 'Spam' },
  { value: 'inappropriate_content', label: 'Inappropriate Content' },
  { value: 'other', label: 'Other' },
];

// Matches the mobile smart timestamp format exactly.
function formatMessageTime(timestamp) {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - msgDay) / 86400000);

  const timeStr = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
  if (diffDays === 0) return timeStr;
  if (diffDays === 1) return `Yesterday ${timeStr}`;
  if (diffDays < 7) {
    return `${date.toLocaleDateString([], { weekday: 'short' })} ${timeStr}`;
  }
  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${timeStr}`;
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', padding: '2px 0' }}>
      <div className="typing-dot typing-dot-1" />
      <div className="typing-dot typing-dot-2" />
      <div className="typing-dot typing-dot-3" />
    </div>
  );
}

function ChatRoomPage() {
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [jobTitle, setJobTitle] = useState('Chat');
  const [workerName, setWorkerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [jobStatus, setJobStatus] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  const [contactWarning, setContactWarning] = useState(null);

  // Translation — always ON for employers.
  const [showOriginal, setShowOriginal] = useState(false);

  // Edit state
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editText, setEditText] = useState('');
  const [hoveredMessageId, setHoveredMessageId] = useState(null);

  // HC-REPORT-001/HC-BLOCK-001 (Apple 1.2, Prompt #278)
  const [actionMenuMessageId, setActionMenuMessageId] = useState(null);
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  // Disclaimer state
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const messagesEndRef = useRef(null);
  // HC-UNREAD-COUNTER follow-up: tracks message count across getMessages
  // snapshot emissions so markConversationAsRead only re-fires on a
  // genuinely new message, not on every emission/re-render. A ref (not
  // state) since updating it must never itself trigger a re-render.
  const previousMessageCountRef = useRef(-1);

  useEffect(() => {
    const loadDetails = async () => {
      try {
        const convDoc = await getDoc(doc(db, 'conversations', conversationId));
        if (!convDoc.exists()) return;

        const data = convDoc.data();
        const loadedJobId = data.jobId || null;
        const loadedCandidateId = data.candidateId || null;

        setJobTitle(data.jobTitle || 'Chat');

        if (loadedJobId) {
          const jobDoc = await getDoc(doc(db, 'jobs', loadedJobId));
          if (jobDoc.exists()) setJobStatus(jobDoc.data().status || null);
        }

        const appSnap = await getDocs(
          // Prompt #451: employerId added so this query is provable by the
          // owner branch of the tightened applications read rule on its own,
          // without leaning on the conversation-participant branch kept for
          // the frozen +99 mobile binary.
          query(
            collection(db, 'applications'),
            where('conversationId', '==', conversationId),
            where('employerId', '==', user.uid),
            limit(1)
          )
        );
        if (!appSnap.empty) setApplicationStatus(appSnap.docs[0].data().status || null);

        if (loadedCandidateId) {
          // HC-INFRA-008C, Prompt #422 — reads the public projection
          // (publicWorkerProfiles/{uid}, Prompt #419), not the raw
          // users/{uid} doc. displayName is the only name field this
          // projection ever carries (no email) — missing-projection falls
          // back to the empty string, same as before.
          const workerDoc = await getDoc(doc(db, 'publicWorkerProfiles', loadedCandidateId));
          if (workerDoc.exists()) {
            const workerData = workerDoc.data();
            setWorkerName(workerData.displayName || '');
          }
        }
      } catch (error) {
        console.error('Error loading conversation details:', error);
      }
    };

    if (conversationId && user) {
      loadDetails();
    }

    // HC-UNREAD-COUNTER follow-up: reset per-conversation on every effect
    // run (i.e. every time conversationId/user changes) — mirrors mobile's
    // _previousMessageCount, which effectively starts fresh per
    // conversation since each one gets its own screen instance.
    previousMessageCountRef.current = -1;

    const unsubscribe = getMessages(conversationId, (msgs) => {
      setMessages(msgs);
      setLoading(false);

      // Re-fire markConversationAsRead when a genuinely NEW message
      // arrives while this conversation stays open — otherwise the
      // one-time mark-as-read on mount leaves the badge incorrectly
      // incremented for a message the employer is actively viewing.
      // Guard logic extracted to chatMarkAsReadRefire.js (pure, directly
      // unit-tested) — same guard shape as mobile's
      // chat_room_screen.dart _previousMessageCount check.
      const { shouldRefire, nextPreviousCount } = resolveMarkAsReadRefire(
        previousMessageCountRef.current,
        msgs.length
      );
      previousMessageCountRef.current = nextPreviousCount;
      if (shouldRefire && conversationId && user) {
        markConversationAsRead(conversationId, user.uid);
      }
    });

    // Listen for unread employer contact warnings.
    const notifQuery = query(
      collection(db, 'users', user.uid, 'notifications'),
      where('type', '==', 'employer_contact_warning'),
      where('read', '==', false)
    );
    const unsubNotif = onSnapshot(notifQuery, (snap) => {
      if (!snap.empty) {
        const notifDoc = snap.docs[0];
        setContactWarning({ id: notifDoc.id, ...notifDoc.data() });
      } else {
        setContactWarning(null);
      }
    });

    // HC-BLOCK-001 (Apple 1.2, Prompt #278): live subscription to the
    // employer's OWN user doc for blockedUserIds — no equivalent listener
    // existed in this file before (unlike mobile's chat_room_screen.dart,
    // which already had a live users/{uid} subscription for accountStatus
    // to extend). A change here re-renders with the same already-fetched
    // `messages` state re-filtered through filterBlockedMessages() — this
    // is what makes block RETROACTIVE here too, not just for messages
    // arriving after the block.
    const unsubBlockedUserIds = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      setBlockedUserIds(snap.data()?.blockedUserIds || []);
    });

    return () => {
      unsubscribe();
      unsubNotif();
      unsubBlockedUserIds();
    };
  }, [conversationId, user]);

  useEffect(() => {
    if (conversationId && user) {
      markConversationAsRead(conversationId, user.uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, user?.uid]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!sessionStorage.getItem('chatDisclaimerShown')) {
      setDisclaimerOpen(true);
    }
  }, []);

  const dismissDisclaimer = () => {
    sessionStorage.setItem('chatDisclaimerShown', '1');
    setDisclaimerOpen(false);
  };

  const isWithin5Min = (timestamp) => {
    if (!timestamp) return false;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return Date.now() - date.getTime() < 5 * 60 * 1000;
  };

  const handleEditSave = async (messageId) => {
    if (!editText.trim()) return;
    setEditSaving(true);
    try {
      const fns = getFunctions();
      const editMessageFn = httpsCallable(fns, 'editMessage');
      await editMessageFn({ conversationId, messageId, newText: editText.trim() });
      setEditingMessageId(null);
      setEditText('');
    } catch (error) {
      console.error('Error editing message:', error);
      alert('Failed to edit message. Please try again.');
    } finally {
      setEditSaving(false);
    }
  };

  // HC-REPORT-001 (Apple 1.2, Prompt #278). reportedUid is the message's own
  // sender — always the OTHER participant, since the action menu that calls
  // this only ever renders for !isMe messages (see the message list below).
  const handleReport = async (message, reason) => {
    setActionMenuMessageId(null);
    try {
      const fns = getFunctions();
      const reportFn = httpsCallable(fns, 'submitContentReport');
      await reportFn({ conversationId, messageId: message.id, reason });
      alert('Report submitted. Thank you for helping keep WorkrMatch safe.');
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    }
  };

  // HC-BLOCK-001 (Apple 1.2, Prompt #278). window.confirm — a plain Yes/No
  // decision, same low-ceremony tier as this file's existing alert() calls
  // for errors; no custom modal component needed for a binary confirm.
  const handleBlock = async (blockedUid) => {
    setActionMenuMessageId(null);
    if (!window.confirm('Block this user? You will no longer see messages from them, and they will not be able to send you new messages.')) {
      return;
    }
    try {
      const fns = getFunctions();
      const blockFn = httpsCallable(fns, 'blockUser');
      await blockFn({ blockedUid });
      alert('User blocked.');
      // No local setState needed — the blockedUserIds onSnapshot listener
      // above picks up the write and re-filters `messages` automatically.
    } catch (error) {
      console.error('Error blocking user:', error);
      alert('Failed to block user. Please try again.');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;
    const text = messageText.trim();
    setMessageText('');
    try {
      await sendMessage(conversationId, user.uid, text);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    }
  };

  const dismissContactWarning = async () => {
    if (!contactWarning) return;
    try {
      await updateDoc(doc(db, 'users', user.uid, 'notifications', contactWarning.id), { read: true });
    } catch (error) {
      console.error('Error dismissing contact warning:', error);
    }
  };

  // HC-BLOCK-001: retroactive hide, applied once per render — see
  // chatMessageVisibility.js's own doc comment.
  const visibleMessages = filterBlockedMessages(messages, blockedUserIds);

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflowX: 'hidden', maxWidth: '100vw' }}>
      {/* Typing animation styles */}
      <style>{`
        @keyframes typingPulse { 0%,100%{opacity:.2} 50%{opacity:1} }
        .typing-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background-color: rgba(0,0,0,0.45);
        }
        .typing-dot-1 { animation: typingPulse 600ms ease-in-out infinite; }
        .typing-dot-2 { animation: typingPulse 600ms ease-in-out 200ms infinite; }
        .typing-dot-3 { animation: typingPulse 600ms ease-in-out 400ms infinite; }
      `}</style>

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
        borderBottom: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: 1 }}>
          <div
            onClick={() => navigate('/messages')}
            title="Back to Messages"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', color: '#4b5563', flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: '17px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{jobTitle}</h1>
            {workerName && !isMobile && (
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '2px 0 0 0' }}>
                Chatting with {workerName}
              </p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Original / Translation toggle — translation is ON when
              showOriginal is false. HC-CHAT-001: filled orange (matches
              this app's existing active-state accent color, e.g. the
              active tab underline in ManageJobsPage.js) when translation
              is on, plain gray outline when off — previously identical
              styling in both states with no visual distinction at all. */}
          <div
            onClick={() => setShowOriginal(v => !v)}
            title={showOriginal ? 'Show Translation' : 'Show Original'}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '34px',
              height: '34px',
              borderRadius: '8px',
              color: showOriginal ? '#6b7280' : '#E8590C',
              backgroundColor: showOriginal ? 'transparent' : 'rgba(232,89,12,0.12)',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = showOriginal ? '#f3f4f6' : 'rgba(232,89,12,0.18)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = showOriginal ? 'transparent' : 'rgba(232,89,12,0.12)'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="2" y1="12" x2="22" y2="12"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Employer contact warning banner */}
      {contactWarning && (
        <div style={{
          backgroundColor: '#fef9c3',
          borderBottom: '1px solid #fde047',
          padding: '0.75rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}>
          <span style={{ fontSize: '14px', color: '#854d0e' }}>
            Sharing contact information before hire confirmation may pose a security risk to both parties. Please keep communications on WorkrMatch until the hire is confirmed.
          </span>
          <button
            onClick={dismissContactWarning}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #854d0e',
              color: '#854d0e',
              borderRadius: '4px',
              padding: '0.25rem 0.75rem',
              fontSize: '13px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Messages */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '1.5rem',
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: 'column-reverse',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
            Loading messages...
          </div>
        ) : visibleMessages.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
            No messages yet. Start the conversation!
          </div>
        ) : (
          <div>
            {visibleMessages.map((message) => {
              const isMe = message.senderId === user.uid;
              const translation = message.translations?.en;
              const awaitingTranslation = !isMe && !showOriginal && !translation && isTranslationPending(message);
              const displayText = (!isMe && !showOriginal && translation)
                ? translation
                : message.text;
              const isTranslated = !isMe && !showOriginal && translation && translation !== message.text;
              const canEdit = isMe &&
                isWithin5Min(message.timestamp) &&
                message.type !== 'offer_response' &&
                message.type !== 'auto_reply';
              const isEditing = editingMessageId === message.id;
              const isHovered = hoveredMessageId === message.id;
              const hasIndicator = isTranslated || message.edited;

              return (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    justifyContent: isMe ? 'flex-end' : 'flex-start',
                    alignItems: 'flex-end',
                    gap: '4px',
                    marginBottom: '8px',
                  }}
                  onMouseEnter={() => setHoveredMessageId(message.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                >
                  {/* Pencil icon — left of bubble for sent messages */}
                  {canEdit && isHovered && !isEditing && (
                    <button
                      onClick={() => { setEditingMessageId(message.id); setEditText(message.text); }}
                      title="Edit message"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#6b7280',
                        padding: '2px 4px',
                        flexShrink: 0,
                        lineHeight: 1,
                      }}
                    >
                      ✏️
                    </button>
                  )}

                  {/* HC-REPORT-001/HC-BLOCK-001 (Apple 1.2, Prompt #278):
                      "..." action button — same hover-reveal placement as
                      the pencil icon above, but for the OTHER party's
                      messages instead of your own. Opens a small dropdown
                      with the 4 report reasons plus Block, rather than a
                      separate reason-picker modal — one less component for
                      a 5-option list. */}
                  {!isMe && isHovered && (
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        onClick={() => setActionMenuMessageId(actionMenuMessageId === message.id ? null : message.id)}
                        title="Report or block"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#6b7280',
                          padding: '2px 6px',
                          lineHeight: 1,
                        }}
                      >
                        ⋯
                      </button>
                      {actionMenuMessageId === message.id && (
                        <div style={{
                          position: 'absolute',
                          bottom: '28px',
                          left: 0,
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 100,
                          minWidth: '180px',
                          overflow: 'hidden',
                        }}>
                          <div style={{ padding: '8px 12px', fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', borderBottom: '1px solid #f3f4f6' }}>
                            Report
                          </div>
                          {REPORT_REASONS.map((r) => (
                            <button
                              key={r.value}
                              onClick={() => handleReport(message, r.value)}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '8px 12px', fontSize: '13px', color: '#1f2937',
                              }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {r.label}
                            </button>
                          ))}
                          <div style={{ borderTop: '1px solid #f3f4f6' }}>
                            <button
                              onClick={() => handleBlock(message.senderId)}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                background: 'none', border: 'none', cursor: 'pointer',
                                padding: '8px 12px', fontSize: '13px', color: '#dc2626', fontWeight: '500',
                              }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              Block User
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div
                    style={{
                      maxWidth: isMobile ? '85vw' : '75%',
                      backgroundColor: isMe ? '#2196F3' : '#E0E0E0',
                      color: isMe ? 'white' : '#1f2937',
                      padding: '10px 16px',
                      borderRadius: '16px',
                    }}
                  >
                    {isEditing ? (
                      <div>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          autoFocus
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '15px',
                            resize: 'none',
                            outline: 'none',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit',
                            color: '#1f2937',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'flex-end' }}>
                          <button
                            onClick={() => { setEditingMessageId(null); setEditText(''); }}
                            disabled={editSaving}
                            style={{
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              padding: '4px 12px',
                              fontSize: '13px',
                              backgroundColor: 'white',
                              cursor: 'pointer',
                              color: '#374151',
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleEditSave(message.id)}
                            disabled={!editText.trim() || editSaving}
                            style={{
                              backgroundColor: '#E8590C',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '4px 12px',
                              fontSize: '13px',
                              cursor: editText.trim() && !editSaving ? 'pointer' : 'not-allowed',
                              opacity: editText.trim() && !editSaving ? 1 : 0.5,
                            }}
                          >
                            {editSaving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      </div>
                    ) : awaitingTranslation ? (
                      <TypingIndicator />
                    ) : (
                      <>
                        <div style={{ fontSize: '16px', marginBottom: hasIndicator || message.timestamp ? '4px' : 0 }}>
                          {displayText}
                        </div>
                        {hasIndicator && (
                          <div style={{
                            fontSize: '11px',
                            fontStyle: 'italic',
                            marginBottom: message.timestamp ? '2px' : 0,
                            display: 'flex',
                            gap: '8px',
                          }}>
                            {/* HC-CHAT-001 follow-up: matches the header toggle's
                                orange "on" state — isTranslated is already gated on
                                !showOriginal (line 335), so this only ever renders
                                while the current view is actually showing the
                                translation, not merely because one exists.
                                Globe is the same inline SVG as the header toggle
                                (stroke="currentColor") rather than the 🌐 emoji —
                                emoji glyphs render with their own built-in color and
                                ignore CSS `color`, which caused the previous
                                blue-globe/orange-text mismatch. */}
                            {isTranslated && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#E8590C', fontWeight: 600 }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10"/>
                                  <line x1="2" y1="12" x2="22" y2="12"/>
                                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                                </svg>
                                Translated
                              </span>
                            )}
                            {message.edited && <span style={{ color: isMe ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.38)' }}>✏️ Edited</span>}
                          </div>
                        )}
                        {message.timestamp && (
                          <div style={{
                            fontSize: '11px',
                            color: isMe ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.45)',
                            textAlign: isMe ? 'right' : 'left',
                          }}>
                            {formatMessageTime(message.timestamp)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Message Input */}
      {(jobStatus === 'closed' || jobStatus === 'expired') && applicationStatus !== 'hired' ? (
        <div style={{
          backgroundColor: '#E5E7EB',
          color: '#374151',
          textAlign: 'center',
          padding: '12px',
          borderTop: '1px solid #e5e7eb',
          width: '100%',
          boxSizing: 'border-box',
          fontSize: '14px',
        }}>
          This conversation is closed.
        </div>
      ) : (
        <div style={{
          backgroundColor: 'white',
          padding: isMobile ? '0.75rem 1rem' : '1rem 2rem',
          paddingBottom: isMobile ? '16px' : undefined,
          borderTop: '1px solid #e5e7eb',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <form onSubmit={handleSendMessage} style={{ display: 'flex', gap: '1rem' }}>
            <input
              type="text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type a message..."
              style={{
                flex: 1,
                padding: '0.75rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '24px',
                fontSize: '16px',
                outline: 'none',
              }}
            />
            <button
              type="submit"
              disabled={!messageText.trim()}
              style={{
                backgroundColor: '#E8590C',
                color: 'white',
                padding: '0.75rem 2rem',
                borderRadius: '24px',
                border: 'none',
                fontSize: '16px',
                fontWeight: '500',
                cursor: messageText.trim() ? 'pointer' : 'not-allowed',
                opacity: messageText.trim() ? 1 : 0.5,
              }}
            >
              Send
            </button>
          </form>
        </div>
      )}

      {/* Privacy Disclaimer Modal */}
      {disclaimerOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '480px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                Privacy Reminder
              </h2>
            </div>
            <p style={{ fontSize: '14px', color: '#374151', marginBottom: '12px' }}>
              For your protection, please do not share sensitive information
              over chat, including:
            </p>
            {[
              'Social Security Numbers (SSN)',
              'Bank account or routing numbers',
              'Passwords or PINs',
              'Immigration documents',
              'Government ID numbers',
            ].map((item) => (
              <div key={item} style={{
                display: 'flex', gap: '8px', marginBottom: '8px',
                alignItems: 'flex-start',
              }}>
                <span style={{ color: '#f97316', fontWeight: 'bold', flexShrink: 0 }}>•</span>
                <span style={{ fontSize: '13px', color: '#374151' }}>{item}</span>
              </div>
            ))}
            <p style={{
              fontSize: '12px', color: '#6b7280', fontStyle: 'italic',
              marginTop: '12px', marginBottom: '20px',
            }}>
              WorkrMatch is not responsible for sensitive information voluntarily
              shared through chat. Message responsibly.
            </p>
            <button
              onClick={dismissDisclaimer}
              style={{
                width: '100%',
                backgroundColor: '#E8590C',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              I Understand and Agree
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default ChatRoomPage;
